import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select } from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { useWeather, WeatherBadge } from './WeatherBadge';
import { getTeamStatsUrl } from '../utils/teamLinks';
import { TeamStatsModal } from './TeamStatsModal';
import { CalendarView } from './ticketTable/CalendarView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


export const TicketTable = ({ games, onDataChange, refreshTrigger, isMobileView = false, eventId = 'mavericks-2025', appSettings, partnerOptions = [], currentUser = null, currentEvent = null }) => {
  const { isAuthenticated } = useAuth();
  // Initialize ticket data from cache for instant display
  const [ticketData, setTicketData] = useState(() => {
    try {
      const cached = localStorage.getItem(`cachedTicketData_${eventId}`);
      return cached ? JSON.parse(cached) : {};
    } catch { return {}; }
  });
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // Initialize filter and view from sessionStorage for persistence across screen/event switches
  const [filterType, setFilterType] = useState(() => {
    return localStorage.getItem('ticketViewFilter') || 'all';
  });
  const [hidePlayedGames, setHidePlayedGames] = useState(() => {
    return localStorage.getItem('ticketHidePlayed') === 'true';
  });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingChange, setPendingChange] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('ticketViewMode') || 'list';
  }); // 'list' or 'calendar'
  
  // Family Event Add/Delete/Edit state
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [showEditEventModal, setShowEditEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [newEventData, setNewEventData] = useState({
    opponent: '',
    date: '',
    end_date: '',
    timeCT: '',
    endTime: '',
    note: '',
    is_perpetual: false
  });
  const [editEventData, setEditEventData] = useState({
    opponent: '',
    date: '',
    end_date: '',
    timeCT: '',
    endTime: '',
    note: '',
    location: ''
  });
  const [addingEvent, setAddingEvent] = useState(false);
  const [savingEvent, setSavingEvent] = useState(false);
  const [deletingEvent, setDeletingEvent] = useState(null);
  
  // Friends & Family assignment state
  const [friendsFamilyList, setFriendsFamilyList] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningTicket, setAssigningTicket] = useState(null); // { gameIdx, ticketIdx, type: 'tickets'|'parking' }
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifyGameIdx, setNotifyGameIdx] = useState(null);
  
  // Team stats modal
  const [teamStatsModal, setTeamStatsModal] = useState(null);

  // Live games currently in progress for this event's sport.
  // Polled every 60s while the ticket list is mounted so a "LIVE" chip
  // flashes next to any row whose (date, opponent) matches.
  const [liveGames, setLiveGames] = useState([]);
  React.useEffect(() => {
    const sport = (currentEvent?.sport || '').toLowerCase();
    if (!sport || sport === 'family' || sport === 'concerts') {
      setLiveGames([]);
      return;
    }
    let cancelled = false;
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const fetchLive = async () => {
      try {
        const resp = await fetch(`${BACKEND_URL}/api/live/games?sport=${encodeURIComponent(sport)}`);
        if (!resp.ok) return;
        const data = await resp.json();
        if (!cancelled) setLiveGames(data?.games || []);
      } catch { /* ignore */ }
    };
    fetchLive();
    const t = setInterval(fetchLive, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [currentEvent?.sport]);

  // Returns true if the given game row is currently live.
  const isGameLive = React.useCallback((game) => {
    if (!game || !liveGames || liveGames.length === 0) return false;
    const gDate = (game.date || '').slice(0, 10);
    const gOpp = (game.opponent || '').toLowerCase().trim();
    if (!gDate || !gOpp) return false;
    for (const lg of liveGames) {
      if (lg.date !== gDate) continue;
      const names = [lg.home_name, lg.away_name, lg.home_abbrev, lg.away_abbrev]
        .filter(Boolean).map(s => s.toLowerCase());
      // Match if the opponent name is contained in / contains any live-side name
      if (names.some(n => n && (n.includes(gOpp) || gOpp.includes(n)))) return true;
    }
    return false;
  }, [liveGames]);
  
  // Bulk-assign (marquee-select) state — desktop table only.
  // Draw a box around multiple ticket/parking cells and assign them all at once.
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelected, setBulkSelected] = useState({}); // {"gameIdx:type:itemIdx": true}
  const [bulkDrag, setBulkDrag] = useState(null); // {x1,y1,x2,y2} in container-relative coords
  const [bulkPartner, setBulkPartner] = useState('');
  const tableWrapperRef = React.useRef(null);
  
  // Check if this is a family/personal event (non-sports)
  const isFamilyEvent = currentEvent?.sport?.toLowerCase() === 'family';
  // Check if this is a concerts/theater event (non-sports, but has tickets)
  const isConcertsEvent = currentEvent?.sport?.toLowerCase() === 'concerts';
  // Check if this is any non-sports event
  const isNonSportsEvent = isFamilyEvent || isConcertsEvent;
  
  // Persist view preferences to localStorage so they survive across visits/logins
  useEffect(() => {
    localStorage.setItem('ticketViewMode', viewMode);
  }, [viewMode]);
  
  useEffect(() => {
    localStorage.setItem('ticketViewFilter', filterType);
  }, [filterType]);
  
  useEffect(() => {
    localStorage.setItem('ticketHidePlayed', hidePlayedGames.toString());
  }, [hidePlayedGames]);

  // Get dynamic ticket and parking counts from settings
  const totalSeats = appSettings?.total_seats || 6;
  const totalParking = appSettings?.total_parking ?? 2;

  // Load data from backend on mount, when refreshTrigger changes, or when games change
  // Also reload when appSettings changes (to get correct totalSeats)
  useEffect(() => {
    if (games && games.length > 0 && appSettings) {
      loadTicketData();
    } else if (games && games.length > 0 && !appSettings) {
      // Wait for appSettings to load
      setLoading(true);
    } else {
      // For new events with no games, set empty data
      setTicketData({});
      setLoading(false);
    }
  }, [refreshTrigger, games, eventId, appSettings]);

  const loadTicketData = async () => {
    // If we have cached data, show refreshing indicator instead of full loading
    const hasCachedData = Object.keys(ticketData).length > 0;
    if (hasCachedData) {
      setIsRefreshing(true);
    }
    
    try {
      const response = await axios.get(`${API}/tickets/assignments?event_id=${eventId}`);
      const data = response.data.assignments || response.data || {};
      
      // Initialize missing games using game_index as key
      // API returns string keys, so check both string and number keys
      const initializedData = games.reduce((acc, game) => {
        const gameIdx = game.game_index;
        const existingData = data[String(gameIdx)] || data[gameIdx];
        acc[gameIdx] = existingData ? {
          ...existingData,
          // Ensure tickets/parking arrays match current settings size
          tickets: (existingData.tickets || []).slice(0, totalSeats).concat(Array(Math.max(0, totalSeats - (existingData.tickets?.length || 0))).fill('')),
          parking: (existingData.parking || []).slice(0, totalParking).concat(Array(Math.max(0, totalParking - (existingData.parking?.length || 0))).fill(''))
        } : {
          tickets: Array(totalSeats).fill(''),
          parking: Array(totalParking).fill(''),
          notes: '',
          mavs_score: 0,
          opponent_score: 0
        };
        return acc;
      }, {});
      
      setTicketData(initializedData);
      
      // Cache the data for instant load next time
      try {
        localStorage.setItem(`cachedTicketData_${eventId}`, JSON.stringify(initializedData));
      } catch (e) {
        console.warn('Failed to cache ticket data:', e);
      }
      
      if (onDataChange) {
        onDataChange(initializedData);
      }
    } catch (error) {
      console.error('Error loading ticket data:', error);
      // Initialize with empty data if backend fails using game_index as key
      const emptyData = games.reduce((acc, game) => {
        const gameIdx = game.game_index;
        acc[gameIdx] = {
          tickets: Array(totalSeats).fill(''),
          parking: Array(totalParking).fill(''),
          notes: '',
          mavs_score: 0,
          opponent_score: 0
        };
        return acc;
      }, {});
      setTicketData(emptyData);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }

    // Auto-fetch MLB scores for baseball events
    if (currentEvent?.sport?.toLowerCase() === 'baseball') {
      try {
        const scoreResp = await axios.post(`${API}/scores/update-mlb`);
        if (scoreResp.data?.updated > 0) {
          // Re-fetch ticket data to get updated scores
          const refreshResp = await axios.get(`${API}/tickets/assignments?event_id=${eventId}`);
          const refreshData = refreshResp.data.assignments || refreshResp.data || {};
          const refreshedData = games.reduce((acc, game) => {
            const gameIdx = game.game_index;
            const existingData = refreshData[String(gameIdx)] || refreshData[gameIdx];
            acc[gameIdx] = existingData ? {
              ...existingData,
              tickets: (existingData.tickets || []).slice(0, totalSeats).concat(Array(Math.max(0, totalSeats - (existingData.tickets?.length || 0))).fill('')),
              parking: (existingData.parking || []).slice(0, totalParking).concat(Array(Math.max(0, totalParking - (existingData.parking?.length || 0))).fill(''))
            } : acc[gameIdx];
            return acc;
          }, { ...ticketData });
          setTicketData(refreshedData);
          try {
            localStorage.setItem(`cachedTicketData_${eventId}`, JSON.stringify(refreshedData));
          } catch (e) {}
        }
      } catch (e) {
        console.warn('MLB score fetch failed:', e);
      }
    }
  };

  const isGamePlayed = (gameIdx) => {
    const data = ticketData[String(gameIdx)] || ticketData[gameIdx];
    if (!data) return false;
    // Prefer the explicit `played` flag set by auto score updaters (handles
    // legitimate shutouts like DAL 2 - MIN 0). Fallback to both scores > 0
    // for manually entered games where the flag may not yet be set.
    if (data.played === true) return true;
    return (data.mavs_score > 0 && data.opponent_score > 0);
  };

  const handleUpdateAttempt = (gameIdx, type, itemIdx, value) => {
    // Check if this is a played game and user is trying to change tickets/parking
    const isPlayed = isGamePlayed(gameIdx);
    const isChangingAssignments = type === 'tickets' || type === 'parking';
    
    if (isPlayed && isChangingAssignments && isAuthenticated) {
      // Store the pending change and show confirmation
      setPendingChange({ gameIdx, type, itemIdx, value });
      setShowConfirmDialog(true);
    } else {
      // Allow the change without confirmation
      updateTicketData(gameIdx, type, itemIdx, value);
    }
  };

  const confirmChange = () => {
    if (pendingChange) {
      const { gameIdx, type, itemIdx, value } = pendingChange;
      updateTicketData(gameIdx, type, itemIdx, value);
      setPendingChange(null);
    }
    setShowConfirmDialog(false);
  };

  // ============ Bulk-assign (marquee) helpers ============
  const bulkCellKey = (gameIdx, type, itemIdx) => `${gameIdx}:${type}:${itemIdx}`;

  const exitBulkMode = () => {
    setBulkMode(false);
    setBulkSelected({});
    setBulkDrag(null);
    setBulkPartner('');
  };

  const rectsIntersect = (a, b) =>
    !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);

  const recomputeBulkSelection = (x1, y1, x2, y2) => {
    const wrapper = tableWrapperRef.current;
    if (!wrapper) return;
    const marqRect = {
      left: Math.min(x1, x2),
      right: Math.max(x1, x2),
      top: Math.min(y1, y2),
      bottom: Math.max(y1, y2),
    };
    const wrapperBox = wrapper.getBoundingClientRect();
    const next = {};
    wrapper.querySelectorAll('[data-bulk-cell]').forEach((el) => {
      const b = el.getBoundingClientRect();
      // Translate to wrapper-relative coords so scroll offsets don't skew the hit test
      const cellRect = {
        left: b.left - wrapperBox.left + wrapper.scrollLeft,
        right: b.right - wrapperBox.left + wrapper.scrollLeft,
        top: b.top - wrapperBox.top + wrapper.scrollTop,
        bottom: b.bottom - wrapperBox.top + wrapper.scrollTop,
      };
      if (rectsIntersect(marqRect, cellRect)) {
        next[el.getAttribute('data-bulk-cell')] = true;
      }
    });
    setBulkSelected(next);
  };

  const handleBulkMouseDown = (e) => {
    if (!bulkMode) return;
    // Ignore clicks on the toolbar/action bar — only start marquee from the table area
    if (e.target.closest('[data-bulk-noselect]')) return;
    const wrapper = tableWrapperRef.current;
    if (!wrapper) return;
    e.preventDefault();
    const box = wrapper.getBoundingClientRect();
    const x = e.clientX - box.left + wrapper.scrollLeft;
    const y = e.clientY - box.top + wrapper.scrollTop;
    setBulkDrag({ x1: x, y1: y, x2: x, y2: y });
    if (!e.shiftKey) setBulkSelected({});
  };

  const handleBulkMouseMove = (e) => {
    if (!bulkMode || !bulkDrag) return;
    const wrapper = tableWrapperRef.current;
    if (!wrapper) return;
    const box = wrapper.getBoundingClientRect();
    const x = e.clientX - box.left + wrapper.scrollLeft;
    const y = e.clientY - box.top + wrapper.scrollTop;
    setBulkDrag((d) => (d ? { ...d, x2: x, y2: y } : d));
    recomputeBulkSelection(bulkDrag.x1, bulkDrag.y1, x, y);
  };

  const handleBulkMouseUp = () => {
    if (!bulkMode) return;
    setBulkDrag(null);
  };

  const applyBulkAssignment = async (overrideValue) => {
    const keys = Object.keys(bulkSelected);
    if (!keys.length) return;
    // Prefer the explicit override (from one-tap partner buttons) over the
    // stateful bulkPartner so we don't race a setState.
    const value = overrideValue !== undefined ? overrideValue : bulkPartner;
    // Fire updates sequentially so we can log activity per cell and avoid
    // races on the same game_index doc; small (≤ dozens) so this is fine.
    for (const key of keys) {
      const [gi, type, ii] = key.split(':');
      const gameIdx = parseInt(gi, 10);
      const itemIdx = parseInt(ii, 10);
      if (Number.isFinite(gameIdx) && Number.isFinite(itemIdx)) {
        // Bypass the "played game" confirm dialog — the user has explicitly
        // opted in to bulk assignment, so we go straight to the update.
        // eslint-disable-next-line no-await-in-loop
        await updateTicketData(gameIdx, type, itemIdx, value);
      }
    }
    exitBulkMode();
  };
  // =======================================================

  const cancelChange = () => {
    setPendingChange(null);
    setShowConfirmDialog(false);
  };

  // Fetch Friends & Family list
  const fetchFriendsFamily = async () => {
    // Use email as user identifier (no separate id field in user object)
    const userId = currentUser?.email || currentUser?.id;
    if (!userId) return;
    try {
      const response = await axios.get(`${API}/users/${userId}/friends-family`);
      if (response.data.success) {
        setFriendsFamilyList(response.data.contacts || []);
      }
    } catch (error) {
      console.error('Error fetching friends & family:', error);
    }
  };

  // Load Friends & Family on mount
  useEffect(() => {
    const userId = currentUser?.email || currentUser?.id;
    if (userId) {
      fetchFriendsFamily();
    }
  }, [currentUser?.email, currentUser?.id]);

  // Open assignment modal for a ticket
  const openAssignModal = (gameIdx, ticketIdx, type) => {
    setAssigningTicket({ gameIdx, ticketIdx, type });
    setShowAssignModal(true);
  };

  // Assign a contact to a ticket
  const assignContactToTicket = (contact) => {
    if (!assigningTicket) return;
    
    const { gameIdx, ticketIdx, type } = assigningTicket;
    const newData = { ...ticketData };
    
    if (!newData[gameIdx]) {
      newData[gameIdx] = {
        tickets: Array(totalSeats).fill(''),
        parking: Array(totalParking).fill(''),
        notes: '',
        mavs_score: 0,
        opponent_score: 0,
        ticket_assignments: {},
        parking_assignments: {}
      };
    }
    
    // Initialize assignments objects if not exist
    if (!newData[gameIdx].ticket_assignments) {
      newData[gameIdx].ticket_assignments = {};
    }
    if (!newData[gameIdx].parking_assignments) {
      newData[gameIdx].parking_assignments = {};
    }
    
    // Store the contact assignment
    if (type === 'tickets') {
      newData[gameIdx].ticket_assignments[ticketIdx] = contact ? {
        contact_id: contact.id,
        name: contact.name,
        email: contact.email,
        phone: contact.phone
      } : null;
    } else {
      newData[gameIdx].parking_assignments[ticketIdx] = contact ? {
        contact_id: contact.id,
        name: contact.name,
        email: contact.email,
        phone: contact.phone
      } : null;
    }
    
    setTicketData(newData);
    
    // Save to backend
    axios.post(`${API}/tickets/assignments?event_id=${eventId}`, {
      game_index: gameIdx,
      tickets: newData[gameIdx].tickets,
      parking: newData[gameIdx].parking,
      notes: newData[gameIdx].notes,
      mavs_score: newData[gameIdx].mavs_score || 0,
      opponent_score: newData[gameIdx].opponent_score || 0,
      ticket_assignments: newData[gameIdx].ticket_assignments,
      parking_assignments: newData[gameIdx].parking_assignments
    }, {
      headers: {
        'X-User-Email': currentUser?.email || '',
        'X-User-Name': currentUser?.name || ''
      }
    }).catch(error => console.error('Error saving assignment:', error));
    
    setShowAssignModal(false);
    setAssigningTicket(null);
  };

  // Get contact assignment for a ticket
  const getTicketAssignment = (gameIdx, ticketIdx, type) => {
    const data = ticketData[String(gameIdx)] || ticketData[gameIdx];
    if (!data) return null;
    
    if (type === 'tickets') {
      return data.ticket_assignments?.[ticketIdx] || null;
    } else {
      return data.parking_assignments?.[ticketIdx] || null;
    }
  };

  // Get notify contacts for a family event game
  const getNotifyContacts = (gameIdx) => {
    const data = ticketData[String(gameIdx)] || ticketData[gameIdx];
    return data?.notify_contacts || [];
  };

  // Toggle a contact for notification on a family event
  const toggleNotifyContact = (gameIdx, contact) => {
    const newData = { ...ticketData };
    if (!newData[gameIdx]) {
      newData[gameIdx] = {
        tickets: Array(totalSeats).fill(''),
        parking: Array(totalParking).fill(''),
        notes: '',
        mavs_score: 0,
        opponent_score: 0,
        notify_contacts: []
      };
    }
    if (!newData[gameIdx].notify_contacts) {
      newData[gameIdx].notify_contacts = [];
    }

    const existing = newData[gameIdx].notify_contacts.findIndex(c => c.contact_id === contact.id);
    if (existing >= 0) {
      newData[gameIdx].notify_contacts.splice(existing, 1);
    } else {
      newData[gameIdx].notify_contacts.push({
        contact_id: contact.id,
        name: contact.name,
        email: contact.email || '',
        phone: contact.phone || ''
      });
    }

    setTicketData(newData);

    // Save to backend
    axios.post(`${API}/tickets/assignments?event_id=${eventId}`, {
      game_index: gameIdx,
      tickets: newData[gameIdx].tickets || [],
      parking: newData[gameIdx].parking || [],
      notes: newData[gameIdx].notes || '',
      mavs_score: newData[gameIdx].mavs_score || 0,
      opponent_score: newData[gameIdx].opponent_score || 0,
      ticket_assignments: newData[gameIdx].ticket_assignments || {},
      parking_assignments: newData[gameIdx].parking_assignments || {},
      notify_contacts: newData[gameIdx].notify_contacts || []
    }, {
      headers: {
        'X-User-Email': currentUser?.email || '',
        'X-User-Name': currentUser?.name || ''
      }
    }).catch(error => console.error('Error saving notify contacts:', error));
  };

  // Open notify modal for a family event game
  const openNotifyModal = (gameIdx) => {
    setNotifyGameIdx(gameIdx);
    setShowNotifyModal(true);
  };

  const updateTicketData = async (gameIdx, type, itemIdx, value) => {
    const newData = { ...ticketData };
    if (!newData[gameIdx]) {
      newData[gameIdx] = {
        tickets: Array(totalSeats).fill(''),
        parking: Array(totalParking).fill(''),
        notes: '',
        location: '',
        mavs_score: 0,
        opponent_score: 0
      };
    }
    
    if (type === 'notes') {
      newData[gameIdx].notes = value;
    } else if (type === 'location') {
      newData[gameIdx].location = value;
    } else if (type === 'mavs_score' || type === 'opponent_score') {
      newData[gameIdx][type] = parseInt(value) || 0;
    } else {
      newData[gameIdx][type][itemIdx] = value;
    }
    
    setTicketData(newData);
    
    // Notify parent component of data change
    if (onDataChange) {
      onDataChange(newData);
    }

    // Save to backend with event_id and user info
    try {
      await axios.post(`${API}/tickets/assignments?event_id=${eventId}`, {
        game_index: gameIdx,
        tickets: newData[gameIdx].tickets,
        parking: newData[gameIdx].parking,
        notes: newData[gameIdx].notes,
        location: newData[gameIdx].location || '',
        mavs_score: newData[gameIdx].mavs_score || 0,
        opponent_score: newData[gameIdx].opponent_score || 0
      }, {
        headers: {
          'X-User-Email': currentUser?.email || '',
          'X-User-Name': currentUser?.name || ''
        }
      });
      // Keep games_schedule.location and games_schedule.note in sync so that
      // the Event Admin bulk-sync path does not silently wipe them out and
      // so the game document itself stays canonical for these fields.
      if (type === 'location' || type === 'notes') {
        try {
          const patchBody = type === 'location'
            ? { location: value || '' }
            : { note: value || '' };
          await axios.patch(`${API}/games/${gameIdx}`, patchBody, {
            params: { event_id: eventId }
          });
        } catch (e) {
          console.error('Failed to sync games_schedule field:', e);
        }
      }
    } catch (error) {
      console.error('Error saving ticket data:', error);
    }
  };

  const getTicketStats = (gameIdx) => {
    const data = ticketData[gameIdx] || { tickets: Array(totalSeats).fill(''), parking: Array(totalParking).fill('') };
    const usedTickets = data.tickets.filter(t => t !== '').length;
    const usedParking = data.parking.filter(p => p !== '').length;
    return { usedTickets, usedParking };
  };

  // Add new family event
  const handleAddFamilyEvent = async () => {
    if (!newEventData.opponent || !newEventData.date) {
      alert('Please enter event name and date');
      return;
    }
    
    // Validate end_date is not before start date if provided
    if (newEventData.end_date && newEventData.end_date < newEventData.date) {
      alert('End date cannot be before start date');
      return;
    }
    
    setAddingEvent(true);
    try {
      const response = await axios.post(`${API}/games/`, {
        opponent: newEventData.opponent,
        date: newEventData.date,
        end_date: newEventData.end_date || '',
        timeCT: newEventData.timeCT || '',
        endTime: newEventData.endTime || '',
        note: newEventData.note || '',
        location: newEventData.location || '',
        type: 'regular',
        season_type: 'regular',
        is_perpetual: newEventData.is_perpetual || false
      }, {
        params: { 
          event_id: eventId,
          ...(((isFamilyEvent || isConcertsEvent) && currentUser?.email) ? { user_email: currentUser.email } : {})
        }
      });
      
      if (response.data.success) {
        // If a note was entered, also save it to ticket_assignments so the list view shows it
        // (list view reads notes from ticket_assignments.notes, not games_schedule.note)
        const createdNote = (newEventData.note || '').trim();
        if (createdNote) {
          const newGameIdx = response.data.game?.game_index;
          if (typeof newGameIdx === 'number') {
            try {
              await axios.post(`${API}/tickets/assignments?event_id=${eventId}`, {
                game_index: newGameIdx,
                notes: createdNote
              }, {
                headers: {
                  'X-User-Email': currentUser?.email || '',
                  'X-User-Name': currentUser?.name || ''
                }
              });
            } catch (e) {
              console.error('Failed to save note into ticket_assignments:', e);
            }
          }
        }
        // Reset form and close modal
        setNewEventData({ opponent: '', date: '', end_date: '', timeCT: '', endTime: '', note: '', location: '' });
        setShowAddEventModal(false);
        // Trigger refresh
        if (onDataChange) {
          onDataChange(ticketData);
        }
        // Force page reload to show new event
        window.location.reload();
      }
    } catch (error) {
      console.error('Error adding event:', error);
      alert('Failed to add event. Please try again.');
    } finally {
      setAddingEvent(false);
    }
  };

  // Delete family event
  const handleDeleteFamilyEvent = async (gameIndex) => {
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }
    
    setDeletingEvent(gameIndex);
    try {
      const response = await axios.delete(`${API}/games/${gameIndex}`, {
        params: { event_id: eventId }
      });
      
      if (response.data.success) {
        // Trigger refresh
        if (onDataChange) {
          onDataChange(ticketData);
        }
        // Force page reload to show updated list
        window.location.reload();
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event. Please try again.');
    } finally {
      setDeletingEvent(null);
    }
  };

  // Open edit modal for family/concert event
  const handleEditEvent = (game) => {
    setEditingEvent(game);
    setEditEventData({
      opponent: game.opponent || '',
      date: game.date || '',
      end_date: game.end_date || '',
      timeCT: game.timeCT || game.time || '',
      endTime: game.endTime || '',
      // Prefer ticket_assignments notes/location (updated via inline edits) but
      // fall back to games_schedule fields so the modal never shows blank when
      // only the game doc has the value.
      note: ticketData[game.game_index]?.notes || game.note || '',
      location: ticketData[game.game_index]?.location || game.location || ''
    });
    setShowEditEventModal(true);
  };

  // Save edited event
  const handleSaveEditEvent = async () => {
    if (!editingEvent) return;
    
    // Validate end_date is not before start date if provided
    if (editEventData.end_date && editEventData.end_date < editEventData.date) {
      alert('End date cannot be before start date');
      return;
    }
    
    setSavingEvent(true);
    try {
      // Update game details (opponent, date, end_date, time, endTime, location, note)
      // We send `note` here as well so games_schedule stays canonical and
      // survives bulk sync/backup paths.
      const gameUpdateResponse = await axios.patch(`${API}/games/${editingEvent.game_index}`, {
        opponent: editEventData.opponent,
        date: editEventData.date,
        end_date: editEventData.end_date || '',
        timeCT: editEventData.timeCT,
        endTime: editEventData.endTime || '',
        location: editEventData.location || '',
        note: editEventData.note || ''
      }, {
        params: { event_id: eventId }
      });
      
      // Also mirror note & location into ticket_assignments so inline table
      // cells (which read from ticketData first) stay in sync.
      await updateTicketData(editingEvent.game_index, 'notes', null, editEventData.note || '');
      await updateTicketData(editingEvent.game_index, 'location', null, editEventData.location || '');
      
      if (gameUpdateResponse.data.success) {
        setShowEditEventModal(false);
        setEditingEvent(null);
        // Force page reload to show updated data
        window.location.reload();
      } else {
        alert('Failed to save: ' + (gameUpdateResponse.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving event:', error);
      alert('Failed to save event. Please try again.');
    } finally {
      setSavingEvent(false);
    }
  };

  const formatDate = (dateString) => {
    // Parse the date as local time to avoid timezone shifts
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateCompact = (dateString) => {
    // Compact format for mobile: "Sat Oct 11"
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
    const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${weekday} ${monthDay}`;
  };

  const formatTime = (timeCT) => {
    if (!timeCT) return 'TBD';
    
    // If already in 12-hour format with AM/PM, return as is
    if (timeCT.toLowerCase().includes('am') || timeCT.toLowerCase().includes('pm')) {
      return timeCT;
    }
    
    // Convert 24-hour format (HH:MM) to 12-hour format
    try {
      const [hours, minutes] = timeCT.split(':').map(Number);
      if (isNaN(hours) || isNaN(minutes)) return timeCT;
      
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch {
      return timeCT;
    }
  };

  const filteredGames = games
    .filter(game => {
      // Parse game date for upcoming filter
      const parseLocalDate = (dateString) => {
        if (!dateString) return new Date(0);
        const parts = dateString.split('-');
        if (parts.length !== 3) return new Date(0);
        const [year, month, day] = parts.map(Number);
        return new Date(year, month - 1, day);
      };
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const gameDate = parseLocalDate(game.date);
      const isUpcoming = gameDate >= today;
      
      const matchesSearch = game.opponent.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           formatDate(game.date).toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = filterType === 'all' || game.type === filterType;
      // "Show Upcoming Only" is purely date-based so a future game with no
      // score (e.g. B-assigned Nov 1 Arizona) always appears. Earlier logic
      // mis-indexed ticketData by array position and dropped matching games.
      const matchesPlayedFilter = !hidePlayedGames || isUpcoming;
      
      return matchesSearch && matchesFilter && matchesPlayedFilter;
    })
    .sort((a, b) => {
      // Sort by date chronologically
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA - dateB;
    });

  const getSelectClass = (value) => {
    if (!value) return '';
    // Alternate colors for different partners
    const colorClasses = [
      'bg-primary/20 text-primary border-primary',
      'bg-warning/30 text-warning border-warning font-semibold',
      'bg-green-500/20 text-green-600 border-green-500',
      'bg-purple-500/20 text-purple-600 border-purple-500',
      'bg-orange-500/20 text-orange-600 border-orange-500',
    ];
    const index = partnerOptions.findIndex(p => p.id === value);
    if (index >= 0) {
      return colorClasses[index % colorClasses.length];
    }
    return 'bg-muted text-muted-foreground';
  };

  return (
    <>
    <Card className={`w-full ${isMobileView ? 'max-w-md mx-auto' : ''}`}>
      <CardContent className={`${isMobileView ? 'pt-4' : 'pt-6'}`}>
        {/* View Mode Tabs */}
        <div className="mb-4 flex gap-2 border-b border-border pb-2">
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
            className="flex items-center gap-2"
          >
            📋 List View
          </Button>
          <Button
            variant={viewMode === 'calendar' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('calendar')}
            className="flex items-center gap-2"
          >
            📅 Calendar View
          </Button>
        </div>

        {/* Calendar View */}
        {viewMode === 'calendar' ? (
          <>
          <CalendarView 
            games={games}
            ticketData={ticketData}
            isAuthenticated={isAuthenticated}
            hidePlayedGames={hidePlayedGames}
            onToggleHidePlayed={() => setHidePlayedGames(!hidePlayedGames)}
            totalSeats={totalSeats}
            totalParking={totalParking}
            partnerOptions={partnerOptions}
            isMobileView={isMobileView}
            currentEvent={currentEvent}
          />
          {(isFamilyEvent || isConcertsEvent) && isAuthenticated && (
            <div className="flex justify-center mt-4">
              <Button
                onClick={() => setShowAddEventModal(true)}
                className="bg-green-600 hover:bg-green-700"
                data-testid="add-event-calendar-btn"
              >
                + Add Event
              </Button>
            </div>
          )}
          </>
        ) : (
          <>
        {/* Mobile View - Card Layout */}
        {isMobileView ? (
          <div className="space-y-2">
            {/* Filter Button and Add Button for Mobile */}
            <div className="mb-3 flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setHidePlayedGames(!hidePlayedGames)}
                className="flex-1 h-9"
              >
                {hidePlayedGames ? 'Show All' : 'Upcoming Only'}
              </Button>
              {(isFamilyEvent || isConcertsEvent) && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => setShowAddEventModal(true)}
                  className="h-9"
                >
                  ➕ Add
                </Button>
              )}
            </div>
            
            {filteredGames.map((game, chronologicalIdx) => {
              const gameIdx = game.game_index; // Use actual game_index from database
              const data = ticketData[String(gameIdx)] || ticketData[gameIdx] || { tickets: Array(totalSeats).fill(''), parking: Array(totalParking).fill(''), notes: '', mavs_score: 0, opponent_score: 0 };
              const { usedTickets, usedParking } = getTicketStats(gameIdx);
              
              const mavsScore = data.mavs_score || 0;
              const oppScore = data.opponent_score || 0;
              // Both scores must be entered before we call it a Win / Loss —
              // a lone "2 - A" (opponent score missing) was being flagged as a
              // W, which is misleading when the user just hasn't finished data entry.
              const hasScore = !isNonSportsEvent && (data.played === true || (mavsScore > 0 && oppScore > 0));
              const isWin = hasScore && mavsScore > oppScore;
              const isLoss = hasScore && oppScore > mavsScore;
              const isTie = hasScore && mavsScore === oppScore;
              
              return (
                <Card key={gameIdx} className={`${hasScore ? 'bg-primary/10 border-primary/30' : ''}`}>
                  <CardContent className="p-2">
                    {/* Game Header - Single Line */}
                    <div className="flex items-start justify-between mb-1.5 gap-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {!isNonSportsEvent && (
                          <Badge variant={game.type === 'preseason' ? 'secondary' : game.type === 'playoff' || game.season_type === 'playoff' ? 'destructive' : 'default'} className="text-xs py-0.5 px-2 flex-shrink-0 flex items-center gap-1">
                            {game.type === 'preseason' ? (
                              <>🎯 Preseason</>
                            ) : game.type === 'playoff' || game.season_type === 'playoff' ? (
                              <>🏆 Playoff</>
                            ) : (
                              <>📅 Regular</>
                            )}
                          </Badge>
                        )}
                        {isFamilyEvent && (
                          <span className="text-lg flex-shrink-0">🕐</span>
                        )}
                        {isConcertsEvent && (
                          <span className="text-lg flex-shrink-0">🎭</span>
                        )}
                        <span className="text-xs font-semibold flex-shrink-0">#{chronologicalIdx + 1}</span>
                        <div className="text-sm font-bold truncate flex items-center gap-1">
                          {isNonSportsEvent ? game.opponent : `vs ${game.opponent}`}
                          {game.is_perpetual && <span className="inline-flex items-center px-1 py-0 rounded text-[8px] font-bold bg-amber-500/20 text-amber-400 border border-amber-500/40 flex-shrink-0">Annual</span>}
                          {isGameLive(game) && (
                            <span
                              className="inline-flex items-center gap-0.5 px-1 py-0 rounded text-[8px] font-bold text-white bg-red-600 animate-pulse flex-shrink-0"
                              title="This game is currently in progress"
                            >
                              <span className="w-1 h-1 rounded-full bg-white"></span>
                              LIVE
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground text-right whitespace-nowrap">
                        {formatDateCompact(game.date)}
                        <br />
                        {formatTime(game.timeCT)}
                      </div>
                    </div>
                    
                    {/* Score - Hide for non-sports events */}
                    {!isNonSportsEvent && (
                      <div className="flex items-center gap-1 mb-1.5 flex-nowrap">
                        <Input
                          type="number"
                          placeholder="DAL"
                          value={hasScore ? mavsScore : (mavsScore > 0 ? mavsScore : '')}
                          onChange={(e) => updateTicketData(gameIdx, 'mavs_score', null, e.target.value)}
                          className="text-xs w-12 h-7 text-center px-0.5"
                          min="0"
                          disabled={!isAuthenticated}
                        />
                        <span className="text-xs font-medium">-</span>
                        <Input
                          type="number"
                          placeholder="OPP"
                          value={hasScore ? oppScore : (oppScore > 0 ? oppScore : '')}
                          onChange={(e) => updateTicketData(gameIdx, 'opponent_score', null, e.target.value)}
                          className="text-xs w-12 h-7 text-center px-0.5"
                          min="0"
                          disabled={!isAuthenticated}
                        />
                        {hasScore && (
                          <>
                            <Badge variant={isTie ? 'secondary' : isWin ? 'default' : 'destructive'} className={`text-xs py-0 px-1.5 ml-0.5 ${isTie ? 'bg-amber-500 text-white hover:bg-amber-500' : ''}`}>
                              {isTie ? 'T' : isWin ? 'W' : 'L'}
                            </Badge>
                            {data.ot && (
                              <span
                                data-testid={`ot-badge-${gameIdx}`}
                                className="ml-0.5 inline-flex items-center px-1 py-0 rounded text-[9px] font-bold bg-indigo-500/20 text-indigo-300 border border-indigo-500/40"
                                title="Game went to overtime"
                              >
                                OT
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    )}
                    
                    {/* Tickets & Parking - Hide for family events, show for concerts */}
                    {!isFamilyEvent && (
                      <div className="mb-1.5">
                        <div className="flex justify-between items-center mb-0.5">
                          <label className="text-xs text-muted-foreground">
                            Tickets ({usedTickets}/{totalSeats})
                          </label>
                          <label className="text-xs text-muted-foreground">
                            Parking ({usedParking}/{totalParking})
                          </label>
                        </div>
                        <div className="flex gap-0.5 flex-nowrap justify-between items-start">
                          <div className="flex gap-0.5 flex-nowrap flex-wrap">
                            {Array(totalSeats).fill('').map((_, ticketIdx) => {
                              const assignment = getTicketAssignment(gameIdx, ticketIdx, 'tickets');
                              const hasTicket = data.tickets[ticketIdx] && data.tickets[ticketIdx] !== '';
                              return (
                                <div 
                                  key={ticketIdx}
                                  className={`relative ${assignment ? 'ring-2 ring-green-500 ring-offset-1 rounded' : ''}`}
                                  title={assignment ? `Assigned to: ${assignment.name}` : hasTicket ? 'Click ticket to assign to contact' : ''}
                                >
                                  <Select
                                    value={data.tickets[ticketIdx] || ''}
                                    onChange={(e) => handleUpdateAttempt(gameIdx, 'tickets', ticketIdx, e.target.value)}
                                    className={`text-xs w-7 h-7 ${getSelectClass(data.tickets[ticketIdx])}`}
                                    disabled={!isAuthenticated}
                                  >
                                    <option value="">-</option>
                                    {partnerOptions.map(p => (
                                      <option key={p.id} value={p.id}>{p.id}</option>
                                    ))}
                                  </Select>
                                  {/* Assign button - shows when ticket is allocated */}
                                  {hasTicket && isAuthenticated && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openAssignModal(gameIdx, ticketIdx, 'tickets');
                                      }}
                                      className={`absolute -top-1 -right-1 w-3 h-3 rounded-full text-[8px] font-bold flex items-center justify-center ${
                                        assignment 
                                          ? 'bg-green-500 text-white' 
                                          : 'bg-gray-400 text-white hover:bg-primary'
                                      }`}
                                      title={assignment ? `Assigned to ${assignment.name}` : 'Assign to contact'}
                                      data-testid={`assign-ticket-${gameIdx}-${ticketIdx}`}
                                    >
                                      {assignment ? '✓' : '+'}
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          <div className="flex gap-0.5 flex-nowrap flex-wrap">
                            {Array(totalParking).fill('').map((_, parkingIdx) => {
                              const assignment = getTicketAssignment(gameIdx, parkingIdx, 'parking');
                              const hasParking = data.parking[parkingIdx] && data.parking[parkingIdx] !== '';
                              return (
                                <div 
                                  key={`p-${parkingIdx}`}
                                  className={`relative ${assignment ? 'ring-2 ring-green-500 ring-offset-1 rounded' : ''}`}
                                  title={assignment ? `Assigned to: ${assignment.name}` : hasParking ? 'Click parking to assign to contact' : ''}
                                >
                                  <Select
                                    value={data.parking[parkingIdx] || ''}
                                    onChange={(e) => handleUpdateAttempt(gameIdx, 'parking', parkingIdx, e.target.value)}
                                    className={`text-xs w-7 h-7 ${getSelectClass(data.parking[parkingIdx])}`}
                                    disabled={!isAuthenticated}
                                  >
                                    <option value="">-</option>
                                    {partnerOptions.map(p => (
                                      <option key={p.id} value={p.id}>{p.id}</option>
                                    ))}
                                  </Select>
                                  {/* Assign button - shows when parking is allocated */}
                                  {hasParking && isAuthenticated && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openAssignModal(gameIdx, parkingIdx, 'parking');
                                      }}
                                      className={`absolute -top-1 -right-1 w-3 h-3 rounded-full text-[8px] font-bold flex items-center justify-center ${
                                        assignment 
                                          ? 'bg-green-500 text-white' 
                                          : 'bg-gray-400 text-white hover:bg-primary'
                                      }`}
                                      title={assignment ? `Assigned to ${assignment.name}` : 'Assign to contact'}
                                      data-testid={`assign-parking-${gameIdx}-${parkingIdx}`}
                                    >
                                      {assignment ? '✓' : '+'}
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Notify Contacts for Family Events - Mobile */}
                    {isFamilyEvent && (
                      <div className="flex items-center gap-1 mb-1.5 flex-wrap">
                        <span className="text-[10px] text-muted-foreground mr-1">Notify:</span>
                        {getNotifyContacts(gameIdx).map((nc) => (
                          <span
                            key={nc.contact_id}
                            className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-blue-600/30 text-blue-300 border border-blue-500/40"
                          >
                            {nc.name.split(' ')[0]}
                          </span>
                        ))}
                        {isAuthenticated && (
                          <button
                            onClick={() => openNotifyModal(gameIdx)}
                            className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-bold bg-blue-600/20 text-blue-400 border border-blue-500/30 hover:bg-blue-600/40"
                            data-testid={`notify-btn-mobile-${gameIdx}`}
                          >
                            {getNotifyContacts(gameIdx).length > 0 ? getNotifyContacts(gameIdx).length : '+'}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Notes and Edit/Delete for Family/Concert Events */}
                    <div className="flex gap-2 items-center">
                      <Input
                        placeholder="Add notes..."
                        value={data.notes || ''}
                        onChange={(e) => updateTicketData(gameIdx, 'notes', null, e.target.value)}
                        className="text-xs h-7 flex-1"
                        disabled={!isAuthenticated}
                      />
                      {(isFamilyEvent || isConcertsEvent) && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditEvent(game)}
                            className="h-7 w-7 p-0 text-xs"
                            title="Edit event"
                          >
                            ✏️
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteFamilyEvent(gameIdx)}
                            disabled={deletingEvent === gameIdx}
                            className="h-7 w-7 p-0 text-xs"
                            title="Delete event"
                          >
                            {deletingEvent === gameIdx ? '...' : '🗑️'}
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          /* Desktop View - Table Layout */
          <div
            className="overflow-x-auto relative"
            ref={tableWrapperRef}
            onMouseDown={handleBulkMouseDown}
            onMouseMove={handleBulkMouseMove}
            onMouseUp={handleBulkMouseUp}
            onMouseLeave={handleBulkMouseUp}
            style={{ cursor: bulkMode ? 'crosshair' : undefined, userSelect: bulkMode ? 'none' : undefined }}
          >
            {/* Filter Button and Add Button for Desktop */}
            <div className="mb-4 flex justify-between items-center">
              {(isFamilyEvent || isConcertsEvent) && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => setShowAddEventModal(true)}
                  className="h-9"
                >
                  ➕ Add Event
                </Button>
              )}
              <div className={`${(isFamilyEvent || isConcertsEvent) ? '' : 'ml-auto'} flex items-center gap-2`}>
                {isAuthenticated && !isFamilyEvent && (
                  <Button
                    variant={bulkMode ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => (bulkMode ? exitBulkMode() : setBulkMode(true))}
                    className="h-9"
                    data-bulk-noselect="true"
                    data-testid="bulk-select-toggle"
                    title="Draw a box to assign multiple tickets at once"
                  >
                    {bulkMode ? '✕ Exit Multi-Select' : '🔲 Multi-Select'}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setHidePlayedGames(!hidePlayedGames)}
                  className="h-9"
                  data-bulk-noselect="true"
                >
                  {hidePlayedGames ? 'Show All Events' : 'Show Upcoming Only'}
                </Button>
              </div>
            </div>
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-3 text-sm font-semibold text-muted-foreground">#</th>
                {!isNonSportsEvent && <th className="text-left py-3 px-3 text-sm font-semibold text-muted-foreground">Type</th>}
                <th className="text-left py-3 px-3 text-sm font-semibold text-muted-foreground">Date</th>
                {isFamilyEvent && <th className="text-left py-3 px-3 text-sm font-semibold text-muted-foreground">End Date</th>}
                <th className="text-left py-3 px-3 text-sm font-semibold text-muted-foreground">Time</th>
                <th className="text-left py-3 px-3 text-sm font-semibold text-muted-foreground">{isFamilyEvent ? 'Event/Description' : isConcertsEvent ? 'Show/Event' : 'Opponent'}</th>
                {isConcertsEvent && <th className="text-left py-3 px-3 text-sm font-semibold text-muted-foreground">Location</th>}
                {!isNonSportsEvent && <th className="text-left py-3 px-3 text-sm font-semibold text-muted-foreground">Score</th>}
                {!isFamilyEvent && <th className="text-left py-3 px-3 text-sm font-semibold text-muted-foreground">Tickets ({totalSeats})</th>}
                {!isFamilyEvent && <th className="text-left py-3 px-3 text-sm font-semibold text-muted-foreground">Parking ({totalParking})</th>}
                {!isNonSportsEvent && <th className="text-left py-3 px-3 text-sm font-semibold text-muted-foreground">Status</th>}
                <th className="text-left py-3 px-3 text-sm font-semibold text-muted-foreground">Notes</th>
                {isFamilyEvent && <th className="text-left py-3 px-3 text-sm font-semibold text-muted-foreground">Notify</th>}
                {(isFamilyEvent || isConcertsEvent) && <th className="text-center py-3 px-3 text-sm font-semibold text-muted-foreground">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredGames.map((game, chronologicalIdx) => {
                const gameIdx = game.game_index; // Use actual game_index from database
                const data = ticketData[String(gameIdx)] || ticketData[gameIdx] || { tickets: Array(totalSeats).fill(''), parking: Array(totalParking).fill(''), notes: '', mavs_score: 0, opponent_score: 0 };
                const { usedTickets, usedParking } = getTicketStats(gameIdx);
                
                const mavsScore = data.mavs_score || 0;
                const oppScore = data.opponent_score || 0;
                // Require BOTH scores before classifying a game as W/L —
                // otherwise a half-entered row like "2 - A" would flash a W.
                const hasScore = data.played === true || (mavsScore > 0 && oppScore > 0);
                const isWin = hasScore && mavsScore > oppScore;
                const isLoss = hasScore && oppScore > mavsScore;
                const isTie = hasScore && mavsScore === oppScore;
                
                return (
                  <tr key={gameIdx} className={`border-b border-border hover:bg-muted/50 transition-colors ${!isNonSportsEvent && hasScore ? 'bg-primary/10 border-l-4 border-l-primary' : ''}`}>
                    <td className="py-3 px-3 text-sm text-muted-foreground">{chronologicalIdx + 1}</td>
                    {!isNonSportsEvent && (
                      <td className="py-3 px-3">
                        <Badge variant={game.type === 'preseason' ? 'secondary' : game.type === 'playoff' || game.season_type === 'playoff' ? 'destructive' : 'default'} className="text-xs flex items-center gap-1 w-fit">
                          {game.type === 'preseason' ? (
                            <>🎯 Preseason</>
                          ) : game.type === 'playoff' || game.season_type === 'playoff' ? (
                            <>🏆 Playoff</>
                          ) : (
                            <>📅 Regular</>
                          )}
                        </Badge>
                      </td>
                    )}
                    <td className="py-3 px-3 text-sm font-medium">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{formatDate(game.date)}</span>
                        {isGameLive(game) && (
                          <span
                            data-testid={`live-chip-${gameIdx}`}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-white bg-red-600 animate-pulse"
                            title="This game is currently in progress"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-white"></span>
                            LIVE
                          </span>
                        )}
                      </div>
                    </td>
                    {isFamilyEvent && (
                      <td className="py-3 px-3 text-sm text-muted-foreground">
                        {game.end_date ? formatDate(game.end_date) : '-'}
                      </td>
                    )}
                    <td className="py-3 px-3 text-sm text-muted-foreground">{formatTime(game.timeCT)}</td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        {!isNonSportsEvent && <span className="text-sm font-medium">vs</span>}
                        <span className="text-sm font-semibold">{game.opponent}</span>
                        {game.is_perpetual && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-400 border border-amber-500/40">
                            Annual
                          </span>
                        )}
                      </div>
                    </td>
                    {isConcertsEvent && (
                      <td className="py-3 px-2 min-w-[150px]">
                        <Input
                          placeholder="Venue/Location..."
                          value={data.location || game.location || ''}
                          onChange={(e) => updateTicketData(gameIdx, 'location', null, e.target.value)}
                          className="text-sm"
                          disabled={!isAuthenticated}
                        />
                      </td>
                    )}
                    {!isNonSportsEvent && (
                      <td className="py-3 px-1">
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            placeholder="H"
                            value={hasScore ? mavsScore : (mavsScore > 0 ? mavsScore : '')}
                            onChange={(e) => updateTicketData(gameIdx, 'mavs_score', null, e.target.value)}
                            className="text-sm w-10 h-7 text-center px-0.5"
                            min="0"
                            disabled={!isAuthenticated}
                          />
                          <span className="text-xs font-medium text-muted-foreground">-</span>
                          <Input
                            type="number"
                            placeholder="A"
                            value={hasScore ? oppScore : (oppScore > 0 ? oppScore : '')}
                            onChange={(e) => updateTicketData(gameIdx, 'opponent_score', null, e.target.value)}
                            className="text-sm w-10 h-7 text-center px-0.5"
                            min="0"
                            disabled={!isAuthenticated}
                          />
                          {hasScore && (
                            <>
                              <Badge variant={isTie ? 'secondary' : isWin ? 'success' : 'destructive'} className={`text-xs px-1 ${isTie ? 'bg-amber-500 text-white hover:bg-amber-500' : ''}`}>
                                {isTie ? 'T' : isWin ? 'W' : 'L'}
                              </Badge>
                              {data.ot && (
                                <span
                                  className="inline-flex items-center px-1 py-0 rounded text-[9px] font-bold bg-indigo-500/20 text-indigo-300 border border-indigo-500/40"
                                  title="Game went to overtime"
                                >
                                  OT
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    )}
                    {!isFamilyEvent && (
                      <td className="py-3 px-2">
                        <div className="flex gap-1 flex-nowrap">
                          {Array(totalSeats).fill('').map((_, ticketIdx) => {
                            const assignment = getTicketAssignment(gameIdx, ticketIdx, 'tickets');
                            const hasTicket = data.tickets[ticketIdx] && data.tickets[ticketIdx] !== '';
                            return (
                              <div
                                key={ticketIdx}
                                data-bulk-cell={bulkCellKey(gameIdx, 'tickets', ticketIdx)}
                                className={`relative ${assignment ? 'ring-2 ring-green-500 ring-offset-1 rounded' : ''} ${bulkMode && bulkSelected[bulkCellKey(gameIdx, 'tickets', ticketIdx)] ? 'outline outline-2 outline-blue-500 rounded' : ''}`}
                              >
                                <Select
                                  value={data.tickets[ticketIdx] || ''}
                                  onChange={(e) => handleUpdateAttempt(gameIdx, 'tickets', ticketIdx, e.target.value)}
                                  className={`text-sm w-9 h-8 ${getSelectClass(data.tickets[ticketIdx])} ${bulkMode ? 'pointer-events-none' : ''}`}
                                  disabled={!isAuthenticated || bulkMode}
                                >
                                  <option value="">-</option>
                                  {partnerOptions.map(p => (
                                    <option key={p.id} value={p.id}>{p.id}</option>
                                  ))}
                                </Select>
                                {hasTicket && isAuthenticated && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openAssignModal(gameIdx, ticketIdx, 'tickets');
                                    }}
                                    className={`absolute -top-1 -right-1 w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center ${
                                      assignment 
                                        ? 'bg-green-500 text-white' 
                                        : 'bg-gray-400 text-white hover:bg-primary'
                                    }`}
                                    title={assignment ? `Assigned to ${assignment.name}` : 'Assign to contact'}
                                  >
                                    {assignment ? '✓' : '+'}
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <span className="text-xs text-muted-foreground mt-0.5 block">{usedTickets}/{totalSeats}</span>
                      </td>
                    )}
                    {!isFamilyEvent && (
                      <td className="py-3 px-2">
                        <div className="flex gap-1 flex-nowrap">
                          {Array(totalParking).fill('').map((_, parkingIdx) => {
                            const assignment = getTicketAssignment(gameIdx, parkingIdx, 'parking');
                            const hasParking = data.parking[parkingIdx] && data.parking[parkingIdx] !== '';
                            return (
                              <div
                                key={parkingIdx}
                                data-bulk-cell={bulkCellKey(gameIdx, 'parking', parkingIdx)}
                                className={`relative ${assignment ? 'ring-2 ring-green-500 ring-offset-1 rounded' : ''} ${bulkMode && bulkSelected[bulkCellKey(gameIdx, 'parking', parkingIdx)] ? 'outline outline-2 outline-blue-500 rounded' : ''}`}
                              >
                                <Select
                                  value={data.parking[parkingIdx] || ''}
                                  onChange={(e) => handleUpdateAttempt(gameIdx, 'parking', parkingIdx, e.target.value)}
                                  className={`text-sm w-9 h-8 ${getSelectClass(data.parking[parkingIdx])} ${bulkMode ? 'pointer-events-none' : ''}`}
                                  disabled={!isAuthenticated || bulkMode}
                                >
                                  <option value="">-</option>
                                  {partnerOptions.map(p => (
                                    <option key={p.id} value={p.id}>{p.id}</option>
                                  ))}
                                </Select>
                                {hasParking && isAuthenticated && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openAssignModal(gameIdx, parkingIdx, 'parking');
                                    }}
                                    className={`absolute -top-1 -right-1 w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center ${
                                      assignment 
                                        ? 'bg-green-500 text-white' 
                                        : 'bg-gray-400 text-white hover:bg-primary'
                                    }`}
                                    title={assignment ? `Assigned to ${assignment.name}` : 'Assign to contact'}
                                  >
                                    {assignment ? '✓' : '+'}
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <span className="text-xs text-muted-foreground mt-0.5 block">{usedParking}/{totalParking}</span>
                      </td>
                    )}
                    {!isNonSportsEvent && (
                      <td className="py-3 px-2">
                        {usedTickets === totalSeats && usedParking === totalParking ? (
                          <Badge variant="destructive" className="text-xs">Full</Badge>
                        ) : usedTickets > 0 || usedParking > 0 ? (
                          <Badge variant="warning" className="text-xs">Partial</Badge>
                        ) : (
                          <Badge variant="success" className="text-xs">Available</Badge>
                        )}
                      </td>
                    )}
                    <td className="py-3 px-2 min-w-[200px]">
                      <Input
                        placeholder="Add notes..."
                        value={data.notes || game.note || ''}
                        onChange={(e) => updateTicketData(gameIdx, 'notes', null, e.target.value)}
                        className="text-sm"
                        disabled={!isAuthenticated}
                      />
                    </td>
                    {isFamilyEvent && (
                      <td className="py-3 px-2 min-w-[140px]">
                        <div className="flex flex-wrap gap-1 items-center">
                          {getNotifyContacts(gameIdx).map((nc) => (
                            <span
                              key={nc.contact_id}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-600/30 text-blue-300 border border-blue-500/40"
                              title={nc.email || nc.name}
                            >
                              {nc.name.split(' ')[0]}
                            </span>
                          ))}
                          {isAuthenticated && (
                            <button
                              onClick={() => openNotifyModal(gameIdx)}
                              className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold bg-blue-600/20 text-blue-400 border border-blue-500/30 hover:bg-blue-600/40 transition-colors"
                              title="Manage notification contacts"
                              data-testid={`notify-btn-${gameIdx}`}
                            >
                              {getNotifyContacts(gameIdx).length > 0 ? getNotifyContacts(gameIdx).length : '+'}
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                    {(isFamilyEvent || isConcertsEvent) && (
                      <td className="py-3 px-2 text-center">
                        <div className="flex gap-1 justify-center">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditEvent(game)}
                            className="h-8 w-8 p-0"
                            title="Edit event"
                          >
                            ✏️
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteFamilyEvent(gameIdx)}
                            disabled={deletingEvent === gameIdx}
                            className="h-8 w-8 p-0"
                            title="Delete event"
                          >
                            {deletingEvent === gameIdx ? '...' : '🗑️'}
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {filteredGames.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No games found matching your search.</p>
            </div>
          )}

          {/* Marquee rectangle overlay */}
          {bulkMode && bulkDrag && (
            <div
              className="pointer-events-none absolute border-2 border-blue-500 bg-blue-500/15 rounded-sm"
              style={{
                left: Math.min(bulkDrag.x1, bulkDrag.x2),
                top: Math.min(bulkDrag.y1, bulkDrag.y2),
                width: Math.abs(bulkDrag.x2 - bulkDrag.x1),
                height: Math.abs(bulkDrag.y2 - bulkDrag.y1),
              }}
            />
          )}
          </div>
        )}

        {/* Fixed floating action bar — sits above viewport bottom so it can
            never scroll off-screen inside the table. Uses one-tap partner
            buttons (same options as the per-cell Select) so a drag → tap
            flow assigns every highlighted cell in a single click. */}
        {bulkMode && (
          <div
            data-bulk-noselect="true"
            className="fixed left-1/2 -translate-x-1/2 bottom-4 z-[60] flex flex-wrap items-center gap-2 rounded-full border border-blue-500/60 bg-background/95 backdrop-blur px-4 py-2 shadow-xl"
            style={{ maxWidth: 'calc(100vw - 2rem)' }}
          >
            <span className="text-sm font-semibold whitespace-nowrap" data-testid="bulk-selected-count">
              {Object.keys(bulkSelected).length} selected
            </span>
            <span className="text-xs text-muted-foreground hidden md:inline">
              {Object.keys(bulkSelected).length
                ? 'Tap a partner to assign all'
                : 'Drag a box across ticket / parking cells'}
            </span>
            <div className="w-px h-6 bg-border mx-1" />
            {partnerOptions.map((p) => (
              <Button
                key={p.id}
                size="sm"
                variant="default"
                disabled={!Object.keys(bulkSelected).length}
                onClick={async () => {
                  setBulkPartner(p.id);
                  await applyBulkAssignment(p.id);
                }}
                className="h-8 min-w-[2.25rem] px-3 font-bold"
                data-testid={`bulk-assign-${p.id}`}
                title={`Assign selected to ${p.id}${p.name ? ` (${p.name})` : ''}`}
              >
                {p.id}
              </Button>
            ))}
            <Button
              size="sm"
              variant="outline"
              disabled={!Object.keys(bulkSelected).length}
              onClick={async () => {
                setBulkPartner('');
                await applyBulkAssignment('');
              }}
              className="h-8"
              data-testid="bulk-unassign"
              title="Clear the selected cells"
            >
              — Unassign —
            </Button>
            <div className="w-px h-6 bg-border mx-1" />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setBulkSelected({})}
              disabled={!Object.keys(bulkSelected).length}
              className="h-8"
            >
              Clear
            </Button>
            <Button size="sm" variant="ghost" onClick={exitBulkMode} className="h-8">
              ✕
            </Button>
          </div>
        )}
        </>
        )}
      </CardContent>
    </Card>

    {/* Confirmation Dialog for Editing Played Games */}
    <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
      <DialogContent className="sm:max-w-md border-2 border-cyan-400/70"
        style={{
          boxShadow: '0 0 15px rgba(34, 211, 238, 0.4), 0 0 30px rgba(34, 211, 238, 0.2), 0 0 45px rgba(34, 211, 238, 0.1)'
        }}
      >
        <DialogHeader>
          <DialogTitle>Edit Played Game?</DialogTitle>
          <DialogDescription>
            This game has already been played and has scores entered. Are you sure you want to change the ticket or parking assignments?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={cancelChange}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmChange}
          >
            Yes, Change It
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    
    {/* Add Family Event Modal */}
    <Dialog open={showAddEventModal} onOpenChange={setShowAddEventModal}>
      <DialogContent className="sm:max-w-md border-2 border-cyan-400/70"
        style={{
          boxShadow: '0 0 15px rgba(34, 211, 238, 0.4), 0 0 30px rgba(34, 211, 238, 0.2), 0 0 45px rgba(34, 211, 238, 0.1)'
        }}
      >
        <DialogHeader>
          <DialogTitle>Add New Event</DialogTitle>
          <DialogDescription>
            Add a new event to your family calendar
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium mb-1 block">{isFamilyEvent ? 'Event Name' : 'Show/Event Name'} *</label>
            <Input
              placeholder={isFamilyEvent ? "e.g., Doctor Visit, Birthday Party..." : "e.g., Rush Concert, Hamilton..."}
              value={newEventData.opponent}
              onChange={(e) => setNewEventData({...newEventData, opponent: e.target.value})}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium mb-1 block">Start Date *</label>
              <Input
                type="date"
                value={newEventData.date}
                onChange={(e) => setNewEventData({...newEventData, date: e.target.value})}
                className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
                style={{ colorScheme: 'light' }}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">End Date</label>
              <Input
                type="date"
                value={newEventData.end_date}
                min={newEventData.date || undefined}
                onChange={(e) => setNewEventData({...newEventData, end_date: e.target.value})}
                className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
                style={{ colorScheme: 'light' }}
              />
              <p className="text-xs text-muted-foreground mt-1">Optional - for multi-day events</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium mb-1 block">Start Time</label>
              <Input
                type="time"
                value={newEventData.timeCT}
                onChange={(e) => setNewEventData({...newEventData, timeCT: e.target.value})}
                className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
                style={{ colorScheme: 'light' }}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">End Time</label>
              <Input
                type="time"
                value={newEventData.endTime || ''}
                onChange={(e) => setNewEventData({...newEventData, endTime: e.target.value})}
                className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
                style={{ colorScheme: 'light' }}
              />
            </div>
          </div>
          {isConcertsEvent && (
            <div>
              <label className="text-sm font-medium mb-1 block">Location/Venue</label>
              <Input
                placeholder="e.g., Dickies Arena, AAC..."
                value={newEventData.location || ''}
                onChange={(e) => setNewEventData({...newEventData, location: e.target.value})}
              />
            </div>
          )}
          <div>
            <label className="text-sm font-medium mb-1 block">Notes (optional)</label>
            <Input
              placeholder="Additional details..."
              value={newEventData.note}
              onChange={(e) => setNewEventData({...newEventData, note: e.target.value})}
            />
          </div>
          {isFamilyEvent && (
            <div className="flex items-center justify-between p-3 rounded-lg border border-amber-500/30 bg-amber-500/5">
              <div>
                <div className="text-sm font-medium">Repeats Annually</div>
                <div className="text-xs text-muted-foreground">Repeat this event every year (e.g., birthday, anniversary)</div>
              </div>
              <button
                type="button"
                onClick={() => setNewEventData({...newEventData, is_perpetual: !newEventData.is_perpetual})}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  newEventData.is_perpetual ? 'bg-amber-500' : 'bg-gray-600'
                }`}
                data-testid="perpetual-toggle"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  newEventData.is_perpetual ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
          )}
        </div>
        <DialogFooter className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => {
              setShowAddEventModal(false);
              setNewEventData({ opponent: '', date: '', end_date: '', timeCT: '', endTime: '', note: '', location: '', is_perpetual: false });
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddFamilyEvent}
            disabled={addingEvent || !newEventData.opponent || !newEventData.date}
          >
            {addingEvent ? 'Adding...' : 'Add Event'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    
    {/* Edit Event Modal */}
    <Dialog open={showEditEventModal} onOpenChange={setShowEditEventModal}>
      <DialogContent className="sm:max-w-md border-2 border-cyan-400/70"
        style={{
          boxShadow: '0 0 15px rgba(34, 211, 238, 0.4), 0 0 30px rgba(34, 211, 238, 0.2), 0 0 45px rgba(34, 211, 238, 0.1)'
        }}
      >
        <DialogHeader>
          <DialogTitle>Edit Event</DialogTitle>
          <DialogDescription>
            Update the event details
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium mb-1 block">{isFamilyEvent ? 'Event Name' : 'Show/Event Name'} *</label>
            <Input
              placeholder={isFamilyEvent ? "e.g., Doctor Visit, Birthday Party..." : "e.g., Rush Concert, Hamilton..."}
              value={editEventData.opponent}
              onChange={(e) => setEditEventData({...editEventData, opponent: e.target.value})}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium mb-1 block">Start Date *</label>
              <Input
                type="date"
                value={editEventData.date}
                onChange={(e) => setEditEventData({...editEventData, date: e.target.value})}
                className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
                style={{ colorScheme: 'light' }}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">End Date</label>
              <Input
                type="date"
                value={editEventData.end_date || ''}
                min={editEventData.date || undefined}
                onChange={(e) => setEditEventData({...editEventData, end_date: e.target.value})}
                className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
                style={{ colorScheme: 'light' }}
              />
              <p className="text-xs text-muted-foreground mt-1">Optional - for multi-day events</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium mb-1 block">Start Time</label>
              <Input
                type="time"
                value={editEventData.timeCT}
                onChange={(e) => setEditEventData({...editEventData, timeCT: e.target.value})}
                className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
                style={{ colorScheme: 'light' }}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">End Time</label>
              <Input
                type="time"
                value={editEventData.endTime || ''}
                onChange={(e) => setEditEventData({...editEventData, endTime: e.target.value})}
                className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
                style={{ colorScheme: 'light' }}
              />
            </div>
          </div>
          {isConcertsEvent && (
            <div>
              <label className="text-sm font-medium mb-1 block">Location/Venue</label>
              <Input
                placeholder="e.g., Dickies Arena, AAC..."
                value={editEventData.location}
                onChange={(e) => setEditEventData({...editEventData, location: e.target.value})}
              />
            </div>
          )}
          <div>
            <label className="text-sm font-medium mb-1 block">Notes (optional)</label>
            <Input
              placeholder="Additional details..."
              value={editEventData.note}
              onChange={(e) => setEditEventData({...editEventData, note: e.target.value})}
            />
          </div>
        </div>
        <DialogFooter className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => {
              setShowEditEventModal(false);
              setEditingEvent(null);
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEditEvent}
            disabled={savingEvent || !editEventData.opponent || !editEventData.date}
          >
            {savingEvent ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Friend/Family Assignment Modal */}
    <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
      <DialogContent className="sm:max-w-md border-2 border-cyan-400/70"
        style={{
          boxShadow: '0 0 15px rgba(34, 211, 238, 0.4), 0 0 30px rgba(34, 211, 238, 0.2), 0 0 45px rgba(34, 211, 238, 0.1)'
        }}
      >
        <DialogHeader>
          <DialogTitle>Assign Ticket to Contact</DialogTitle>
          <DialogDescription>
            Select a friend or family member to assign this {assigningTicket?.type === 'parking' ? 'parking pass' : 'ticket'} to.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          {friendsFamilyList.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">
              <p className="mb-2">No contacts yet.</p>
              <p className="text-sm">Add contacts in the Friends & Family section of your profile.</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {/* Current assignment indicator */}
              {assigningTicket && getTicketAssignment(assigningTicket.gameIdx, assigningTicket.ticketIdx, assigningTicket.type) && (
                <div className="p-3 bg-green-50 dark:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-800 mb-3">
                  <div className="text-sm font-medium text-green-800 dark:text-green-200">
                    Currently assigned to:
                  </div>
                  <div className="text-sm text-green-700 dark:text-green-300">
                    {getTicketAssignment(assigningTicket.gameIdx, assigningTicket.ticketIdx, assigningTicket.type)?.name}
                  </div>
                </div>
              )}
              
              {/* Contact list */}
              {friendsFamilyList.map((contact) => (
                <button
                  key={contact.id}
                  onClick={() => assignContactToTicket(contact)}
                  className="w-full p-3 text-left rounded-lg border border-border hover:bg-primary/10 hover:border-primary transition-colors"
                  data-testid={`assign-contact-${contact.id}`}
                >
                  <div className="font-medium">{contact.name}</div>
                  {(contact.email || contact.phone) && (
                    <div className="text-sm text-muted-foreground">
                      {contact.email && <span>{contact.email}</span>}
                      {contact.email && contact.phone && <span className="mx-1">•</span>}
                      {contact.phone && <span>{contact.phone}</span>}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        <DialogFooter className="flex gap-2">
          {assigningTicket && getTicketAssignment(assigningTicket.gameIdx, assigningTicket.ticketIdx, assigningTicket.type) && (
            <Button
              variant="destructive"
              onClick={() => assignContactToTicket(null)}
              data-testid="remove-assignment-btn"
            >
              Remove Assignment
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setShowAssignModal(false)}
            data-testid="cancel-assign-btn"
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Notify Contacts Modal for Family Events */}
    <Dialog open={showNotifyModal} onOpenChange={setShowNotifyModal}>
      <DialogContent className="sm:max-w-md border-2 border-blue-400/70"
        style={{
          boxShadow: '0 0 15px rgba(59, 130, 246, 0.4), 0 0 30px rgba(59, 130, 246, 0.2)'
        }}
      >
        <DialogHeader>
          <DialogTitle>Notification Contacts</DialogTitle>
          <DialogDescription>
            Select friends & family to notify about this event.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          {friendsFamilyList.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">
              <p className="mb-2">No contacts yet.</p>
              <p className="text-sm">Add contacts in the Friends & Family section of your profile.</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {friendsFamilyList.map((contact) => {
                const isSelected = getNotifyContacts(notifyGameIdx).some(nc => nc.contact_id === contact.id);
                return (
                  <button
                    key={contact.id}
                    onClick={() => toggleNotifyContact(notifyGameIdx, contact)}
                    className={`w-full p-3 text-left rounded-lg border transition-colors ${
                      isSelected
                        ? 'bg-blue-600/20 border-blue-500 ring-2 ring-blue-400/50'
                        : 'border-border hover:bg-primary/10 hover:border-primary'
                    }`}
                    data-testid={`notify-contact-${contact.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{contact.name}</div>
                        {contact.email && (
                          <div className="text-sm text-muted-foreground">{contact.email}</div>
                        )}
                      </div>
                      {isSelected && (
                        <span className="text-blue-400 text-lg font-bold">&#10003;</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setShowNotifyModal(false)}
            data-testid="close-notify-modal-btn"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Team Stats Modal */}
    {teamStatsModal && (
      <TeamStatsModal
        teamName={teamStatsModal.teamName}
        sport={teamStatsModal.sport}
        espnUrl={teamStatsModal.espnUrl}
        onClose={() => setTeamStatsModal(null)}
      />
    )}
    </>
  );
};
